// Wraps the new REST API but returns snake_case fields so the existing
// pages/components don't have to change every property access. This is the
// same shape Supabase used to return — minus the Postgres-side aggregations
// the API does for us now.
import { api } from "@/api/client";

export type TipGroup = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  logo_url: string | null;
  invite_code: string;
  created_by: string;
  created_at: string;
};

export type GroupMember = {
  group_id: string;
  user_id: string;
  role: "admin" | "member";
  joined_at: string;
  username?: string;
  emoji?: string;
  avatar_url?: string | null;
};

function toGroup(g: any): TipGroup {
  return {
    id: g.id,
    name: g.name,
    slug: g.slug,
    description: g.description ?? null,
    logo_url: g.logoUrl ?? null,
    invite_code: g.inviteCode,
    created_by: g.createdById,
    created_at: g.createdAt,
  };
}

export async function loadMyGroups(_userId?: string): Promise<(TipGroup & { my_role: "admin" | "member"; member_count: number })[]> {
  const groups = (await api.groups.mine()) as any[];
  return groups.map((g) => ({
    ...toGroup(g),
    my_role: g.role,
    member_count: g.memberCount ?? 0,
  }));
}

export async function loadGroupBySlug(slug: string) {
  const g = await api.groups.bySlug(slug);
  if (!g) return null;
  const members: GroupMember[] = (g.members ?? []).map((m: any) => ({
    group_id: m.groupId,
    user_id: m.userId,
    role: m.role,
    joined_at: m.joinedAt,
    username: m.user?.profile?.username,
    emoji: m.user?.profile?.emoji,
    avatar_url: m.user?.profile?.avatarUrl ?? null,
  }));
  return { group: toGroup(g), members };
}

export async function createGroup(name: string, description?: string) {
  const g = await api.groups.create(name, description);
  return g.id as string;
}

export type JoinRequestStatus = "pending" | "already_member";

export async function requestJoinGroup(code: string): Promise<{ group_id: string; group_name: string; status: JoinRequestStatus }> {
  const r = await api.groups.requestJoin({ code });
  return { group_id: r.groupId, group_name: r.name, status: r.status as JoinRequestStatus };
}

export async function joinGroup(code: string) {
  const r = await api.groups.requestJoin({ code });
  return r.groupId;
}

export async function requestJoinGroupById(groupId: string) {
  const r = await api.groups.requestJoin({ groupId });
  return { group_id: r.groupId, group_name: r.name, status: r.status as JoinRequestStatus };
}

export type JoinRequest = {
  id: string;
  group_id: string;
  user_id: string;
  status: "pending" | "approved" | "rejected";
  created_at: string;
  username?: string;
  emoji?: string;
  avatar_url?: string | null;
};

export async function loadJoinRequests(groupId: string): Promise<JoinRequest[]> {
  const list = (await api.groups.listRequests(groupId)) as any[];
  return list.map((r) => ({
    id: r.id,
    group_id: r.groupId,
    user_id: r.userId,
    status: r.status,
    created_at: r.createdAt,
    username: r.user?.profile?.username,
    emoji: r.user?.profile?.emoji,
    avatar_url: r.user?.profile?.avatarUrl ?? null,
  }));
}

export async function loadMyPendingRequests(_userId?: string) {
  // No dedicated endpoint yet — the page renders [] when this returns []. The
  // Group page derives its own pending state from `requestJoinGroupById`'s
  // response on demand, so this only affects the Groups list view.
  return [] as Array<{ id: string; group_id: string; status: string; created_at: string; group: any }>;
}

export async function approveJoinRequest(id: string) {
  await api.groups.approveRequest(id);
}

export async function rejectJoinRequest(id: string) {
  await api.groups.rejectRequest(id);
}

export async function lookupGroup(code: string) {
  try {
    const g = await api.groups.lookup(code);
    return {
      id: g.id,
      name: g.name,
      slug: g.slug,
      logo_url: g.logoUrl ?? null,
      member_count: g.memberCount,
    };
  } catch {
    return null;
  }
}

export async function regenerateInvite(groupId: string) {
  const r = await api.groups.regenerateCode(groupId);
  return r.code;
}

export async function setMemberRole(groupId: string, userId: string, role: "admin" | "member") {
  await api.groups.setMemberRole(groupId, userId, role);
}

export async function removeMember(groupId: string, userId: string) {
  // Admin-only on the server side. Server also blocks removing the last
  // admin and self-removal (use leaveGroup for that).
  await api.groups.removeMember(groupId, userId);
}

export async function leaveGroup(groupId: string, _userId?: string) {
  await api.groups.leave(groupId);
}

export async function uploadGroupLogo(groupId: string, file: File) {
  const r = await api.groups.uploadLogo(groupId, file);
  return r.url;
}
